"use client";
import { useRef, useState, useEffect, useCallback } from "react";
import { Calendar, Clock, MapPin, ArrowRight, ArrowLeft } from "lucide-react";
import OptimizedImage from "@/components/ui/OptimizedImage";
import Image from "next/image";
import Link from "next/link";

// Simple auto-play carousel using CSS scroll snap + JS loop for minimal layout thrash
const images = [
  { src: "/images/events2.webp", alt: "Event 1" },
  { src: "/images/events5.webp", alt: "Event 4" },
  { src: "/images/events3.webp", alt: "Event 2" },
  { src: "/images/events4.webp", alt: "Event 3" },
  // { src: "/images/seminar6.webp", alt: "Event 2" },
  // { src: "/images/seminar2.webp", alt: "Event 3" },
  // { src: "/images/seminar1.webp", alt: "Event 1" },
  // { src: "/images/seminar3.webp", alt: "Event 4" },
  // { src: "/images/seminar7.webp", alt: "Event 5" },
  // { src: "/images/seminar4.webp", alt: "Event 6" },
];

export default function Events(){
  const trackRef = useRef(null);
  const [index,setIndex]=useState(0);
  const timerRef=useRef(null);
  const event =false
  const setTimer = useCallback(()=>{
    if(timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(()=> setIndex(i=> (i+1)%images.length), 4000);
  },[]);

  useEffect(()=>{ setTimer(); return ()=> timerRef.current && clearInterval(timerRef.current); },[setTimer]);

  useEffect(()=>{ const el=trackRef.current; if(el) el.style.setProperty('--i', index.toString()); },[index]);

  const go = useCallback((i)=>{ setIndex((prev)=> ( (i%images.length)+images.length )%images.length ); setTimer(); },[setTimer]);
  const next = ()=> go(index+1);
  const prev = ()=> go(index-1);

  // Pause on hover / focus for accessibility
  const pause=()=> { if(timerRef.current) { clearInterval(timerRef.current); timerRef.current=null; } };
  const resume=()=> { if(!timerRef.current) setTimer(); };

  return (
      <section id="events" className="relative py-10 bg-[#1f2643]">
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 -translate-x-1/2 top-10 w-[780px] h-[780px] opacity-25 bg-[radial-gradient(circle_at_center,rgba(255,122,24,0.25),transparent_70%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-2 gap-20 items-start">
            {/* Carousel */}
            <div className="flex flex-col">
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-brand-gradient mb-6  sm:mt-0">Recent Highlights</h2>
              <div
                onMouseEnter={pause}
                onMouseLeave={resume}
                onFocus={pause}
                onBlur={resume}
                className="relative group"
                aria-roledescription="carousel"
                aria-label="Recent highlight images"
              >
                <div className="relative w-full overflow-hidden rounded-3xl  bg-[linear-gradient(145deg,var(--blue-bg-alt),var(--blue-surface))]">
                  <div
                    ref={trackRef}
                    className="flex transition-transform duration-700 ease-[cubic-bezier(.16,.84,.44,1)] h-full"
                    style={{ transform: `translateX(-${index * 100}%)` }}
                  >
                    {images.map((img, i) => (
                      <div
                        key={i}
                        className="relative  w-full shrink-0 aspect-[16/9]  md:aspect-[15/8]"
                      >
                        <Image
                          src={img.src}
                          alt={img.alt}
                          fill
                          sizes="(min-width:1280px) 50vw, 100vw"
                          className="object-contain "
                          priority={i===0}
                        />
                        {/* Overlay gradient for readability if captions added later */}
                        <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-black/35 via-transparent to-transparent" />
                      </div>
                    ))}
                  </div>
                  {/* Nav buttons */}
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-2">
                    <button
                      onClick={prev}
                      aria-label="Previous slide"
                      className="pointer-events-auto group w-10 h-10 rounded-full bg-black/35 backdrop-blur-sm border border-white/10 hover:bg-black/55 text-white flex items-center justify-center transition"
                    >
                      <ArrowLeft className="w-5 h-5 group-active:scale-90" />
                    </button>
                    <button
                      onClick={next}
                      aria-label="Next slide"
                      className="pointer-events-auto group w-10 h-10 rounded-full bg-black/35 backdrop-blur-sm border border-white/10 hover:bg-black/55 text-white flex items-center justify-center transition"
                    >
                      <ArrowRight className="w-5 h-5 group-active:scale-90" />
                    </button>
                  </div>
                  {/* Indicators */}
                  <div className="absolute bottom-3 left-4 flex gap-2">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => go(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        className={`h-2.5 rounded-full transition-all ${index === i ? 'w-6 bg-white' : 'w-2.5 bg-white/30 hover:bg-black/50'}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Upcoming Event with registration form */}
            <div className="relative">
              <h3 className="text-3xl md:text-5xl font-semibold tracking-tight text-brand mb-6">Upcoming Events</h3>
              <div className="inline-flex
               absolute w-full text-white font-semibold top-7 left-0 z-10  h-full text-center items-center justify-center  rounded-3xl   " >
                {/* Coming Soon... */}
                <Link target="_blank" className="underline text-xl text-blue-500" href="https://forms.gle/tqVdTz8DFartyWbs8">Apply Now</Link>
                </div>
              <div className=" relative rounded-3xl overflow-hidden blur-sm bg-[#0a0e1d]/50 p-8 backdrop-blur-sm shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_8px_30px_-10px_rgba(0,0,0,0.5)]">
              
                <div className="flex flex-col gap-5">
                  <div className="flex flex-wrap gap-5 text-sm text-white/70">
                    <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-brand" /> 12 Sept 2025</span>
                    <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-brand" /> 14:00 UTC</span>
                    <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-brand" /> Remote / Live</span>
                  </div>
                  <h4 className="text-xl font-semibold text-brand-gradient tracking-tight">AI Project Sprint Workshop</h4>
                  <p className="text-white/70 text-sm leading-relaxed">A focused 90‑minute guided sprint where we break down a real-world AI project brief, iterate on architecture and ship a prototype scaffold live.</p>

                  {/* Form */}
                  <EventForm />
                </div>
              </div>
            </div>
          </div>
        </div>
  </section>
  );
}

function EventForm(){
  const [state,setState]=useState({name:"",email:"",phone:""});
  const [submitting,setSubmitting]=useState(false);
  const [done,setDone]=useState(false);

  const onChange=e=> setState(s=>({...s,[e.target.name]:e.target.value}));

  const valid = state.name.trim() && /.+@.+\..+/.test(state.email) && /[0-9+\-() ]{6,}/.test(state.phone);

  const onSubmit= async e=>{
    e.preventDefault();
    if(!valid) return;
    setSubmitting(true);
    await new Promise(r=> setTimeout(r, 900)); // simulate network
    setSubmitting(false); setDone(true);
    setTimeout(()=> setDone(false), 4000);
  };

  return (
    <form onSubmit={onSubmit} className="mt-4 space-y-4" noValidate>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Name" name="name" value={state.name} onChange={onChange} placeholder="Your name" autoComplete="name" />
        <Field label="Email" name="email" type="email" value={state.email} onChange={onChange} placeholder="you@example.com" autoComplete="email" />
        <Field label="Contact" name="phone" value={state.phone} onChange={onChange} placeholder="+1 555 012 2233" autoComplete="tel" />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <button disabled={!valid||submitting||done} className="inline-flex items-center gap-2 rounded-full px-6 h-11 text-sm font-semibold text-white bg-brand-gradient disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 shadow shadow-brand-glow focus:outline-none focus-visible:ring-2 focus-visible:ring-brand transition">
          <span>{done? 'Registered' : submitting? 'Submitting...' : 'Register'}</span>
          <ArrowRight className={`w-4 h-4 transition-transform ${submitting? 'animate-pulse' : done? 'scale-110' : 'group-hover:translate-x-1'}`} />
        </button>
        <p className="text-xs text-white/40">Free limited seats • Confirmation email after approval</p>
      </div>
    </form>
  );
}

function Field({label,name,value,onChange,type='text',placeholder,autoComplete}){
  return (
    <label className="block text-xs font-semibold tracking-wide text-white/70 space-y-1">
      <span className="uppercase">{label}</span>
      <input
        name={name}
        value={value}
        onChange={onChange}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full h-11 px-4 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[color-mix(in_srgb,var(--brand)_35%,var(--blue-border))] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand text-sm text-white placeholder:text-white/30 transition"
      />
    </label>
  );
}